import React, { useEffect, useState } from 'react'
import axios from '../../services/axios';
import Header from '../../components/utility/Header';
import StatsGrid from '../../components/ui/StatsGrid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showErrorToast, showSuccessToast } from '../../components/ui/Toast';
import { Avatar } from '@mui/material';
import { FaFile, FaGlobe } from 'react-icons/fa';
import Loader from '../../components/Loaders/Loader';
import { useNavigate } from 'react-router-dom';
import PortfolioReview from '../../components/Reviews/PortfolioReview';
import ScreeningReview from '../../components/Reviews/ScreeningReview';
import DesignTaskReview from '../../components/Reviews/DesignTaskReview';
import RoundReview from '../../components/Reviews/RoundReview';
import StyledCard from '../../components/Cards/StyledCard';
import Container from '../../components/Cards/Container';
import IconWrapper from '../../components/Cards/IconWrapper';
import { Briefcase, ChevronDown, ChevronUp, Folder, FolderOpen, MonitorDot, PauseCircle, PenTool, Users } from 'lucide-react';
import ReviewsFilter from '../../components/Filters/ReviewsFilter'; // Import the new filter
import { getRoute, ROUTE_KEY } from '../../config/permissions.config';
import { useAuthContext } from '../../context/AuthProvider';
import { fetchAssignedCandidates, fetchUnderReviewStats, submitReview } from '../../services/dr.service';
import { AiCommentsContent, parseAiReasoningSection } from '../../utility/aiReasoningParser.jsx';
import Modal from '../../components/Modals/Modal';
import { ACTION_TYPES } from '../../utility/ActionTypes';
import { parkCandidateStatus } from '../../services/admin.candidate.service';

const getPortfolioAiScore = (candidate) => {
  const jobProfile = candidate?.currentApplication?.jobProfile;
  const aiScore = candidate?.currentApplication?.stageStatuses?.Portfolio?.aiScore;
  const aiTriggerStatus = candidate?.currentApplication?.aiTriggerStatus;
  if (jobProfile !== 'Brand Designer' || typeof aiScore !== 'number') return null;
  if (
    aiTriggerStatus === 'awaiting_discovery' ||
    aiTriggerStatus === 'permanently_failed'
  ) {
    return null;
  }
  return aiScore;
};

/** Truncated first Strength + Show more → full Strengths/Gaps (TaskDetails / GlobalStaging pattern). */
const PortfolioAiCommentsStrip = ({ aiReasoning }) => {
  const [showMore, setShowMore] = useState(false);
  const strengthsContent = parseAiReasoningSection(aiReasoning ?? '', 'Strengths:');
  const strengthPoints = strengthsContent.split(';').filter((s) => s.trim());
  const firstStrength = strengthPoints[0]?.trim() ?? '';
  const gapsContent = parseAiReasoningSection(aiReasoning ?? '', 'Gaps:');
  const hasExpandable = strengthPoints.length > 1 || !!gapsContent;

  if (!firstStrength && !gapsContent) return null;

  return (
    <div className="px-4 pb-3">
      {showMore ? (
        <AiCommentsContent aiReasoning={aiReasoning} showTitle={false} />
      ) : (
        firstStrength && (
          <p className="typography-small-p text-font-main overflow-hidden whitespace-nowrap text-ellipsis">
            {firstStrength}
          </p>
        )
      )}
      {hasExpandable && (
        <p
          onClick={(e) => {
            e.stopPropagation();
            setShowMore(!showMore);
          }}
          className="self-end cursor-pointer typography-small-p text-font-gray text-start flex gap-1 items-center mt-1"
        >
          {showMore ? (
            <>
              <ChevronUp size={16} /> Hide
            </>
          ) : (
            <>
              <ChevronDown size={16} /> Show more
            </>
          )}
        </p>
      )}
    </div>
  );
};

const statsOne = [
  { title: 'Total', value: 0, icon: () => <IconWrapper size={10} isTeritiaryIcon icon={Users} /> },
  { title: 'Portfolio', value: 0, icon: () => <IconWrapper size={10} isTeritiaryIcon icon={Folder} /> },
  { title: 'Screening', value: 0, icon: () => <IconWrapper size={10} isTeritiaryIcon icon={MonitorDot} /> },
  { title: 'Design Task', value: 0, icon: () => <IconWrapper size={10} isTeritiaryIcon icon={PenTool} /> },
  { title: 'Round 1', value: 0, icon: () => <IconWrapper size={10} isTeritiaryIcon icon={Briefcase} /> },
  { title: 'Round 2', value: 0, icon: () => <IconWrapper size={10} isTeritiaryIcon icon={Briefcase} /> },
  { title: 'Offer Sent', value: 0, icon: () => <IconWrapper size={10} isTeritiaryIcon icon={PenTool} /> },
]

const Round1Review = (props) => <RoundReview roundNumber={1} {...props} />;
const Round2Review = (props) => <RoundReview roundNumber={2} {...props} />;


const Reviews = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [groupedCandidates, setGroupedCandidates] = useState({});
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    jobName: [],
    stage: [],
    'job Type' : [],
    'job Profile' : [],
  });
  const [isParkModalOpen, setIsParkModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Fetch candidates — avoid focus refetch during review (compounds AI batch DB load)
  const { data: candidates, isLoading, isError, error } = useQuery({
    queryKey: ['assignedCandidates'],
    queryFn: fetchAssignedCandidates,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const parkMutation = useMutation({
    mutationFn: ({ candidateId, jobId, parkedReason, parkedNote }) =>
      parkCandidateStatus({ candidateId, jobId, parkedReason, parkedNote }),
    onSuccess: () => {
      queryClient.invalidateQueries(['assignedCandidates']);
      queryClient.invalidateQueries(['underReviewStats']);
      showSuccessToast("Moved to Parked", "Candidate has been moved to Parked");
      setIsParkModalOpen(false);
      setSelectedCandidate(null);
    },
    onError: (error) => {
      console.error("Error parking candidate:", error);
      showErrorToast("Error", error?.response?.data?.message || "Failed to move candidate to Parked");
    }
  });

  // Fetch stats — same contention-aware defaults as assigned candidates
  const { data: statsData, isLoading: isStatsLoading, isError: isStatsError, error: statsError } = useQuery({
    queryKey: ['underReviewStats'],
    queryFn: fetchUnderReviewStats,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const groupCandidatesByJobAndStage = (candidates) => {
    return candidates.reduce((jobAcc, candidate) => {
      candidate?.jobApplications.filter(app => app.currentStage !== "Hired").forEach(application => {
        if (!jobAcc[application.jobTitle]) {
          jobAcc[application.jobTitle] = {};
        }
        if (!jobAcc[application.jobTitle][application.currentStage]) {
          jobAcc[application.jobTitle][application.currentStage] = [];
        }
        jobAcc[application.jobTitle][application.currentStage].push({ ...candidate, currentApplication: application });
      });

      // Sorting data based on JobTitle
      const sortedJobAcc = Object.fromEntries(
        Object.keys(jobAcc).sort().map(key => [key, jobAcc[key]])
      );

      return sortedJobAcc;
    }, {});
  };

  // Apply filters function
  const applyFilters = (candidates, filters, searchTerm) => {
    let filtered = candidates || [];

    // Apply search filter
    if (searchTerm) {
      let regex = new RegExp(searchTerm, "i");
      filtered = filtered.filter(candidate => {
        return regex.test(candidate.firstName) || 
               regex.test(candidate.lastName) || 
               regex.test(candidate.email);
      });
    }

    // Apply job name filter
    if (filters.jobName && filters.jobName.length > 0) {
      filtered = filtered.filter(candidate => {
        return candidate.jobApplications?.some(application => 
          filters.jobName.includes(application.jobTitle)
        );
      });
    }

    // Apply stage filter
    if (filters.stage && filters.stage.length > 0) {
      filtered = filtered.filter(candidate => {
        return candidate.jobApplications?.some(application => 
          filters.stage.includes(application.currentStage)
        );
      });
    }

    // Apply job Type filter
    if (filters['job Type'] && filters['job Type'].length > 0) {
      filtered = filtered.filter(candidate => {
        return candidate.jobApplications?.some(application => 
          filters['job Type'].includes(application.jobType)
        );
      });
    }

    // Apply job Profile filter
    if (filters['job Profile'] && filters['job Profile'].length > 0) {
      filtered = filtered.filter(candidate => {
        return candidate.jobApplications?.some(application => 
          filters['job Profile'].includes(application.jobProfile)
        );
      });
    }

    return filtered;
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setAppliedFilters(newFilters);
  };

  // Handle search changes
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Update filtered candidates when filters or search term changes
  useEffect(() => {
    if (candidates?.length > 0) {
      const filtered = applyFilters(candidates, appliedFilters, searchTerm);
      setFilteredCandidates(filtered);
      setGroupedCandidates(groupCandidatesByJobAndStage(filtered));
    }
  }, [candidates, appliedFilters, searchTerm]);

  // Initial setup when candidates are loaded
  useEffect(() => {
    if (candidates?.length > 0 && !isLoading) {
      setFilteredCandidates(candidates);
      setGroupedCandidates(groupCandidatesByJobAndStage(candidates));
    }
  }, [candidates, isLoading]);

  const submitReviewMutation = useMutation({
    mutationFn: submitReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignedCandidates'] });
      queryClient.invalidateQueries({ queryKey: ['underReviewStats'] });
      showSuccessToast('Review Submitted', 'Your review has been successfully submitted.');
    },
    onError: (error) => {
      showErrorToast('Submission Failed', error.response?.data?.message || 'An error occurred while submitting your review.');
    },
  });

  const handleReviewSubmit = (candidateId, reviewData) => {
    submitReviewMutation.mutate({ candidateId, reviewData });
  };

  const renderReviewComponent = (candidate, jobProfile) => {
    switch (candidate.currentApplication.currentStage) {
      case 'Portfolio':
        return <PortfolioReview candidate={candidate} onSubmit={handleReviewSubmit} />;
      case 'Screening':
        return <ScreeningReview jobProfile={jobProfile} candidate={candidate} onSubmit={handleReviewSubmit} />;
      case 'Design Task':
        return <DesignTaskReview candidate={candidate} onSubmit={handleReviewSubmit} />;
      case 'Round 1':
        return <Round1Review candidate={candidate} onSubmit={handleReviewSubmit} />;
      case 'Round 2':
        return <Round2Review candidate={candidate} onSubmit={handleReviewSubmit} />;
      default:
        return null;
    }
  };

  // Show loader if data is loading
  if (isLoading || isStatsLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader />
      </div>
    );
  }

  if (isError) return <div>Error: {error.message}</div>;

  // Prepare statsOne object with real data
  const updatedStatsOne = statsOne.map((stat) => {
    const foundStat = statsData?.find(s => s.stage === stat.title);
    return { ...stat, value: foundStat ? foundStat.count : 0 };
  });

  // Define the order of stages
  const stageOrder = ['Portfolio', 'Design Task', 'Screening', 'Round 1'];

  // This is for opening the portfolios in different tab
  const ensureAbsoluteUrl = (url) => {
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const handleNavigate = (candidate) => {
    navigate(`${getRoute(user?.role,ROUTE_KEY.REVIEWS_VIEW_CANDIDATE)}/${candidate._id}/${candidate.currentApplication.jobId}`);
  }

  const handleParkClick = (e, candidate) => {
    e.stopPropagation();
    setSelectedCandidate(candidate);
    setIsParkModalOpen(true);
  };

  const handleParkConfirm = (candidate, parkedReason, parkedNote) => {
    if (!candidate) return;
    parkMutation.mutate({
      candidateId: candidate._id,
      jobId: candidate.currentApplication.jobId,
      parkedReason,
      parkedNote,
    });
  };

  const groupedEntries = candidates?.length === 0 ? [] : filteredCandidates?.length > 0 ? Object.entries(groupedCandidates) : [];

  return (
    <Container>
      <Header HeaderText="Reviews" />
      <StyledCard backgroundColor={"bg-background-90"} padding={2}>
        <div className="w-full">
          <StatsGrid stats={updatedStatsOne} />
        </div>
        
        <div className='flex gap-4 items-center mt-4 w-full mb-8 '>
          <div className='w-[20%]'>
            <input
              type="text"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          
          {/* Add the ReviewsFilter component */}
          <ReviewsFilter 
            onApplyFilters={handleFilterChange}
            candidates={candidates || []}
          />
        </div>
        {groupedEntries?.length > 0 ? groupedEntries.map(([jobTitle, stages, jobProfile]) => (
          <div key={jobTitle} className="mb-8">
            <h2 className="mt-4">{jobTitle}</h2>
            {stageOrder.map(stage => {
              if (stages[stage] && stages[stage].length > 0) {
                return (
                  <div key={stage} >
                    <h3 className="mb-4">{stage}</h3>
                    {stages[stage].map(candidate => {
                      const portfolioAiScore = getPortfolioAiScore(candidate);
                      const portfolioAiReasoning =
                        candidate.currentApplication?.stageStatuses?.Portfolio?.aiReasoning;

                      return (
                      <div key={`${candidate._id}-${candidate.currentApplication.jobId}`} className="mb-4 flex flex-col bg-background-100 rounded-xl">
                        <div className='flex items-center p-4 justify-between cursor-pointer' onClick={() => handleNavigate(candidate)}>
                          <div className='flex items-center gap-4 p-4'>
                            <Avatar alt={candidate?.firstName} src={candidate.profilePictureUrl} />
                            <span className="typography-body">
                              {candidate.firstName} {candidate.lastName}
                            </span>
                            <a href={ensureAbsoluteUrl(candidate.currentApplication?.professionalInfo?.portfolio || candidate.professionalInfo?.portfolio || candidate.portfolio)} target="_blank" rel="noopener noreferrer">
                              <div onClick={(e) => e.stopPropagation()}>
                                <IconWrapper hasBg={true} icon={FolderOpen} />
                              </div>
                            </a>
                            <div onClick={(e) => handleParkClick(e, candidate)}>
                              <IconWrapper hasBg={true} icon={PauseCircle} />
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {portfolioAiScore != null && (
                              <div
                                className="bg-background-80 p-2 px-4 typography-body rounded-xl"
                                onClick={(e) => e.stopPropagation()}
                              >
                                AI {portfolioAiScore}
                              </div>
                            )}
                            <div className="bg-background-80 p-2 px-4 typography-body rounded-xl">
                              {candidate.currentApplication.jobProfile}
                            </div>
                          </div>
                        </div>
                        {portfolioAiScore != null && (
                          <PortfolioAiCommentsStrip aiReasoning={portfolioAiReasoning} />
                        )}
                        {renderReviewComponent(candidate, candidate.currentApplication.jobProfile)}
                      </div>
                      );
                    })}
                  </div>
                );
              }
              return null;
            })}
          </div>
        )) :
          <div className='my-4 flex flex-col items-center justify-center'>
            <h2>No Candidates</h2>
            <p className='typography-small-p text-font-gray'>
              {(appliedFilters.jobName?.length > 0 || appliedFilters.stage?.length > 0 || searchTerm) 
                ? 'No candidates match the selected filters' 
                : 'No candidate assigned for review'}
            </p>
          </div>
        }
      </StyledCard>

      <Modal
        open={isParkModalOpen}
        onClose={() => {
          setIsParkModalOpen(false);
          setSelectedCandidate(null);
        }}
        actionType={ACTION_TYPES.PARK}
        item={selectedCandidate}
        candidateName={`${selectedCandidate?.firstName || ''} ${selectedCandidate?.lastName || ''}`.trim()}
        onConfirm={handleParkConfirm}
        isconfirmButtonDisabled={parkMutation.isPending}
      />
    </Container>
  );
};

export default Reviews;